import React, { useEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { Modal, ModalBody, Form, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const paginatedRows = (res) => (Array.isArray(res?.data) ? res.data : []);

const PERMIT_TYPES = [
  { value: "hot_work", label: "Travail à chaud" },
  { value: "electrical", label: "Électricité" },
  { value: "confined_space", label: "Espace confiné" },
  { value: "working_at_height", label: "Travail en hauteur" },
];

const statusBadge = (s) => {
  const colors = {
    draft: "secondary",
    pending: "info",
    approved: "primary",
    rejected: "danger",
    active: "success",
    closed: "dark",
    expired: "warning",
  };
  return <span className={`badge text-bg-${colors[s] || "secondary"}`}>{s}</span>;
};

const stepAssignee = (st) => st.assigned_user || st.assignedUser;

const HseProcessPermitsPage = () => {
  const location = useLocation();
  const [permits, setPermits] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [checklistEdit, setChecklistEdit] = useState(null);
  const [itemPerPage] = useState(10);
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);

  const { register, handleSubmit, reset } = useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getPermits({ per_page: 100 });
      setPermits(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur chargement permis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const [me, sRes, uRes] = await Promise.all([
          api.me().catch(() => null),
          api.getSites(),
          api.getUsers({ per_page: 300 }),
        ]);
        if (me?.id != null) setCurrentUserId(me.id);
        setSites(sRes.data || []);
        setUsers(paginatedRows(uRes));
      } catch (e) {
        toast.error("Erreur chargement sites / utilisateurs");
      }
    })();
  }, []);

  useEffect(() => {
    setListPage(1);
  }, [listSearch]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const permitTypeLabel = (v) => PERMIT_TYPES.find((t) => t.value === v)?.label || String(v ?? "");
  const filteredPermits = permits.filter((p) => {
    if (!listSearch.trim()) return true;
    const q = norm(listSearch);
    return (
      norm(p.title).includes(q) ||
      norm(p.permit_type).includes(q) ||
      norm(permitTypeLabel(p.permit_type)).includes(q) ||
      norm(p.status).includes(q) ||
      norm(p.site?.name).includes(q) ||
      String(p.id).includes(listSearch.trim())
    );
  });
  const listLast = listPage * itemPerPage;
  const listFirst = listLast - itemPerPage;
  const pagePermits = filteredPermits.slice(listFirst, listLast);

  const openCreate = () => {
    reset({
      site_id: "",
      permit_type: "hot_work",
      title: "",
      description: "",
      supervisor_user_id: "",
      hse_user_id: "",
      final_approver_user_id: "",
      start_at: "",
      end_at: "",
      checklist_text: "Espace dégagé\nExtincteurs disponibles\nÉquipement de protection",
    });
    setCreateOpen(true);
  };

  const onCreate = async (data) => {
    const checklist = (data.checklist_text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((label) => ({ label }));
    const body = {
      site_id: parseInt(data.site_id, 10),
      permit_type: data.permit_type,
      title: data.title,
      description: data.description || null,
      supervisor_user_id: data.supervisor_user_id ? parseInt(data.supervisor_user_id, 10) : null,
      hse_user_id: data.hse_user_id ? parseInt(data.hse_user_id, 10) : null,
      final_approver_user_id: data.final_approver_user_id ? parseInt(data.final_approver_user_id, 10) : null,
      start_at: data.start_at || null,
      end_at: data.end_at || null,
      checklist,
    };
    try {
      await api.createPermit(body);
      toast.success("Permis créé");
      setCreateOpen(false);
      load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    setChecklistEdit(null);
    try {
      const p = await api.getPermit(id);
      setDetail(p);
      if (p.status === "draft") {
        setChecklistEdit(
          (p.checklists || []).map((c) => ({
            id: c.id,
            label: c.label || "",
            is_checked: !!c.is_checked,
          }))
        );
      } else {
        setChecklistEdit(null);
      }
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const pid = q.get("open_permit");
    if (!pid || !/^\d+$/.test(pid)) return;
    let cancelled = false;
    (async () => {
      try {
        await openDetail(parseInt(pid, 10));
      } finally {
        if (!cancelled) {
          q.delete("open_permit");
          const s = q.toString();
          window.history.replaceState({}, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash || ""}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, location.pathname, location.hash]);

  const refreshDetail = async () => {
    if (detail?.id) await openDetail(detail.id);
    load();
  };

  const userLabel = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""} (${u.email})`.trim() : "—");

  return (
    <React.Fragment>
      <Head title="Permis de travail" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Permis de travail (PTW)</BlockTitle>
              <BlockDes className="text-soft">
                Travaux à risque : demande, analyse (checklist), validation superviseur → HSE → approbation finale, activation
                puis clôture contrôlée — pas de travail dangereux sans validation tracée.
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <BlockBetween className="mb-3">
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un permis…"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              {!loading && (
                <BlockDes className="text-soft mt-1 mb-0">
                  <p className="small mb-0">
                    {listSearch
                      ? `${filteredPermits.length} permis sur ${permits.length}`
                      : `${permits.length} permis`}
                  </p>
                </BlockDes>
              )}
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <PermissionGate permission={["Permis de travail - Créer"]}>
                  <li>
                    <Button color="primary" size="md" onClick={openCreate}>
                      <Icon name="plus" />
                      <span>Nouveau permis</span>
                    </Button>
                  </li>
                </PermissionGate>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <Spinner />
              </div>
            </div>
          ) : filteredPermits.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <p className="text-soft mb-0">
                  {listSearch ? "Aucun permis ne correspond à la recherche" : "Aucun permis"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow size="xl">
                      <span className="sub-text">Titre</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Type</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Statut</span>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span className="sub-text">Site</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span className="sub-text">Actions</span>
                    </DataTableRow>
                  </DataTableHead>
                  {pagePermits.map((p) => (
                    <DataTableItem key={p.id}>
                      <DataTableRow size="xl">
                        <span className="tb-lead">{p.title}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-sub">{permitTypeLabel(p.permit_type)}</span>
                      </DataTableRow>
                      <DataTableRow size="md">{statusBadge(p.status)}</DataTableRow>
                      <DataTableRow size="lg">
                        <span className="tb-sub">{p.site?.name || p.site_id}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <button
                              type="button"
                              className="btn btn-trigger btn-icon"
                              title="Ouvrir"
                              onClick={() => openDetail(p.id)}
                            >
                              <Icon name="eye" />
                            </button>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              <div className="card-inner">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={filteredPermits.length}
                  paginate={setListPage}
                  currentPage={listPage}
                />
              </div>
            </>
          )}
        </Block>

        <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)} className="modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Nouveau permis</h5>
            <button type="button" className="close" onClick={() => setCreateOpen(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={handleSubmit(onCreate)}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Site *</label>
                    <select className="form-select" name="site_id" ref={register({ required: true })}>
                      <option value="">—</option>
                      {sites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Type *</label>
                    <select className="form-select" name="permit_type" ref={register({ required: true })}>
                      {PERMIT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group mt-3">
                <label className="form-label">Titre *</label>
                <input className="form-control" name="title" ref={register({ required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" name="description" rows={2} ref={register} />
              </div>
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Superviseur</label>
                  <select className="form-select" name="supervisor_user_id" ref={register}>
                    <option value="">—</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {userLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">HSE</label>
                  <select className="form-select" name="hse_user_id" ref={register}>
                    <option value="">—</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {userLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Approbation finale</label>
                  <select className="form-select" name="final_approver_user_id" ref={register}>
                    <option value="">—</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {userLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6">
                  <label className="form-label">Début</label>
                  <input type="datetime-local" className="form-control" name="start_at" ref={register} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Fin</label>
                  <input type="datetime-local" className="form-control" name="end_at" ref={register} />
                </div>
              </div>
              <div className="form-group mt-3">
                <label className="form-label">Checklist risque (une ligne = un point)</label>
                <textarea className="form-control" name="checklist_text" rows={4} ref={register} />
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Créer
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={!!detail || detailLoading}
          toggle={() => {
            setDetail(null);
            setDetailLoading(false);
            setChecklistEdit(null);
          }}
          className="modal-lg"
        >
          <div className="modal-header">
            <h5 className="modal-title">{detail ? detail.title : "Chargement…"}</h5>
            <button
              type="button"
              className="close"
              onClick={() => {
                setDetail(null);
                setChecklistEdit(null);
              }}
            >
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            {detailLoading && <Spinner />}
            {detail && !detailLoading && (
              <>
                <p className="text-soft small mb-2">
                  Statut : {statusBadge(detail.status)} — Site : {detail.site?.name}
                </p>
                <p>{detail.description}</p>
                <h6 className="mt-3">Checklist</h6>
                {detail.status === "draft" && checklistEdit ? (
                  <>
                    <ul className="list list-sm">
                      {checklistEdit.map((c, idx) => (
                        <li key={c.id ?? `new-${idx}`} className="mb-2">
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input mt-1"
                              checked={!!c.is_checked}
                              onChange={(e) => {
                                const next = [...checklistEdit];
                                next[idx] = { ...next[idx], is_checked: e.target.checked };
                                setChecklistEdit(next);
                              }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={c.label}
                              onChange={(e) => {
                                const next = [...checklistEdit];
                                next[idx] = { ...next[idx], label: e.target.value };
                                setChecklistEdit(next);
                              }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                    <Button
                      color="light"
                      size="sm"
                      className="me-2"
                      onClick={() => setChecklistEdit([...checklistEdit, { label: "", is_checked: false }])}
                    >
                      Ajouter une ligne
                    </Button>
                    <PermissionGate permission={["Permis de travail - Modifier"]}>
                      <Button
                        color="primary"
                        size="sm"
                        className="me-2"
                        onClick={async () => {
                          const checklist = checklistEdit
                            .map((row) => ({
                              ...(row.id ? { id: row.id } : {}),
                              label: (row.label || "").trim(),
                              is_checked: !!row.is_checked,
                            }))
                            .filter((row) => row.label);
                          try {
                            await api.updatePermit(detail.id, { checklist });
                            toast.success("Checklist enregistrée");
                            await refreshDetail();
                          } catch (e) {
                            toast.error(e.message || "Erreur");
                          }
                        }}
                      >
                        Enregistrer la checklist
                      </Button>
                    </PermissionGate>
                  </>
                ) : (
                  <ul className="list list-sm">
                    {(detail.checklists || []).map((c) => (
                      <li key={c.id}>
                        <input type="checkbox" checked={!!c.is_checked} readOnly disabled className="me-2" />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                )}
                <h6 className="mt-3">Étapes</h6>
                <ul className="list list-sm">
                  {(detail.steps || []).map((st) => (
                    <li key={st.id}>
                      {st.step_key} — {st.status} — {userLabel(stepAssignee(st))}
                    </li>
                  ))}
                </ul>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {detail.status === "draft" && (
                    <PermissionGate permission={["Permis de travail - Modifier"]}>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.submitPermit(detail.id);
                            toast.success("Soumis pour approbation");
                            await refreshDetail();
                          } catch (e) {
                            toast.error(e.message || "Erreur");
                          }
                        }}
                      >
                        Soumettre pour approbation
                      </Button>
                    </PermissionGate>
                  )}
                  {detail.status === "pending" &&
                    (detail.steps || []).map((st) => {
                      if (st.status !== "pending") return null;
                      const isAssignee =
                        currentUserId != null && Number(st.assigned_user_id) === Number(currentUserId);
                      return (
                        <div key={st.id} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          {isAssignee ? (
                            <PermissionGate permission={["Permis de travail - Approuver"]}>
                              <Button
                                color="success"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.approvePermitStep(detail.id, st.id, {});
                                    toast.success("Étape approuvée");
                                    await refreshDetail();
                                  } catch (e) {
                                    toast.error(e.message || "Erreur");
                                  }
                                }}
                              >
                                Approuver {st.step_key}
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={async () => {
                                  const comments = window.prompt("Motif du refus ?");
                                  if (!comments) return;
                                  try {
                                    await api.rejectPermitStep(detail.id, st.id, { comments });
                                    toast.success("Refus enregistré");
                                    await refreshDetail();
                                  } catch (e) {
                                    toast.error(e.message || "Erreur");
                                  }
                                }}
                              >
                                Refuser {st.step_key}
                              </Button>
                            </PermissionGate>
                          ) : (
                            <span className="text-soft small">
                              Étape réservée à {userLabel(stepAssignee(st)) || "l’approbateur désigné"}.
                            </span>
                          )}
                        </div>
                      );
                    })}
                  {detail.status === "approved" && (
                    <PermissionGate permission={["Permis de travail - Modifier"]}>
                      <Button
                        color="success"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.activatePermit(detail.id);
                            toast.success("Permis activé");
                            await refreshDetail();
                          } catch (e) {
                            toast.error(e.message || "Erreur");
                          }
                        }}
                      >
                        Activer
                      </Button>
                    </PermissionGate>
                  )}
                  {(detail.status === "active" || detail.status === "approved") && (
                    <PermissionGate permission={["Permis de travail - Modifier"]}>
                      <Button
                        color="light"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.closePermit(detail.id, {});
                            toast.success("Permis clôturé");
                            await refreshDetail();
                          } catch (e) {
                            toast.error(e.message || "Erreur");
                          }
                        }}
                      >
                        Clôturer
                      </Button>
                    </PermissionGate>
                  )}
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessPermitsPage;
